"use client";

/**
 * FormSectionGroup — codified progressive-disclosure primitive for forms.
 *
 * Renders an array of `FormSectionDescriptor`s. Sections of `kind: "always"`
 * are visible cards; sections of `kind: "collapsible"` are accordion items
 * collapsed by default (or auto-opened when `defaultOpen` or `hasValue` is
 * true).
 *
 * Used both by the generic entity create-form factory (via
 * `SimpleTableConfig.createFormSections`) and by bespoke forms like the Log
 * Activity Communication/Task forms. See plan
 * `the-log-activity-modal-enumerated-whisper.md`.
 */

import React, { useMemo } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@wtc/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/lib/hooks/ui/use-media-query";
import type { FormSectionDescriptor } from "./types";
import { SectionHeader } from "./SectionHeader";

export interface FormSectionGroupProps<TFormValues extends FieldValues = FieldValues> {
  /** React Hook Form instance — passed to each section's `render`/`hasValue`. */
  form: UseFormReturn<TFormValues>;

  /** Section descriptors in display order. Always-cards render first, then
   * collapsibles in one Accordion below. */
  sections: ReadonlyArray<FormSectionDescriptor<TFormValues>>;

  /** Allow multiple collapsibles open at once. Default `true`. */
  multiOpen?: boolean;

  /** Optional className for the outer wrapper. */
  className?: string;
}

function AlwaysCard<TFormValues extends FieldValues>({
  section,
  form,
}: {
  section: FormSectionDescriptor<TFormValues>;
  form: UseFormReturn<TFormValues>;
}) {
  return (
    <Card>
      {section.title || section.summary ? (
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            {section.icon ? (
              <section.icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            ) : null}
            <span>{section.title}</span>
          </CardTitle>
          {section.summary ? (
            <p className="text-xs text-muted-foreground">{section.summary}</p>
          ) : null}
        </CardHeader>
      ) : null}
      <CardContent className="space-y-4">{section.render(form)}</CardContent>
    </Card>
  );
}

/**
 * Stable, generic-preserving export.
 *
 * NOTE: not wrapped in `React.memo` because the children passed via
 * `sections[].render(form)` reference `form` directly — React Hook Form's
 * `form` reference is stable per render but inner state changes still need
 * to flow through. Section callers should `useMemo` their section arrays.
 */
export function FormSectionGroup<TFormValues extends FieldValues = FieldValues>({
  form,
  sections,
  multiOpen = true,
  className,
}: FormSectionGroupProps<TFormValues>) {
  // Desktop = ≥768px (matches the Tailwind `md` breakpoint the form grid uses).
  // Correct synchronously at mount for a client-opened create modal, so no flash.
  const isDesktop = useMediaQuery("(min-width: 768px)");

  // A `collapseOnMobileOnly` section is a full always-card on desktop and a
  // collapsed accordion item on mobile.
  const isEffectivelyAlways = (s: FormSectionDescriptor<TFormValues>): boolean =>
    s.kind === "always" || (s.collapseOnMobileOnly === true && isDesktop);

  const collapsibles = useMemo(
    () => sections.filter((s) => !isEffectivelyAlways(s)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sections, isDesktop]
  );

  const alwaysSections = useMemo(
    () => sections.filter((s) => isEffectivelyAlways(s)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sections, isDesktop]
  );

  // Compute which collapsibles open initially: defaultOpen OR has existing value.
  // Captured ONCE on mount so re-renders during editing don't snap sections
  // open/closed against the user.
  const initialOpen = useMemo(() => {
    return collapsibles
      .filter((s) => s.defaultOpen === true || (s.hasValue?.(form) ?? false))
      .map((s) => s.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cn("space-y-4", className)}>
      {alwaysSections.map((section) => (
        <AlwaysCard key={section.id} section={section} form={form} />
      ))}

      {collapsibles.length > 0 ? (
        <Accordion
          type={multiOpen ? "multiple" : "single"}
          collapsible
          defaultValue={multiOpen ? initialOpen : initialOpen[0]}
          className="rounded-md border border-border bg-card"
        >
          {collapsibles.map((section) => {
            const filled = section.hasValue?.(form) ?? false;
            return (
              <AccordionItem
                key={section.id}
                value={section.id}
                className="border-b last:border-b-0 px-4"
              >
                <AccordionTrigger className="hover:no-underline">
                  <SectionHeader
                    icon={section.icon}
                    title={section.title}
                    summary={section.summary}
                    hasValue={filled}
                  />
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-1">{section.render(form)}</div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      ) : null}
    </div>
  );
}
